import { useRouteMatch } from 'react-router';

import { UsersTab } from './UsersTab';
import { getUserName } from '../../utilities';
import { Breadcrumbs } from '../../components';
import { ConfirmModal } from '../../components';
import { AssignUserForm } from './AssignUserForm';
import { useOrganization } from './useOrganization';
import { Button, Loading, Modal } from '../../components';
import { AssignUserRoleForm } from './AssignUserRoleForm';
import { UnassignUserRoleForm } from './UnassignUserRoleForm';
import { Copy, SectionHeader, Status } from '../../components';

type CounterProps = {
  label: string;
  value: React.ReactNode;
};
const Counter: React.FC<CounterProps> = ({ label, value }) => {
  return (
    <div className="flex flex-col gap-y-sm">
      <div className="text-mainText text-tiny font-medium">{label}</div>
      <div className="text-main text-section font-bold">{value}</div>
    </div>
  );
};

const Organization = () => {
  const { params } = useRouteMatch<{ id: string }>();
  const {
    organization,
    users,
    loading,
    isOpened,

    onOpen,
    onClose,
    handleAddUser,
    userOptions,
    updating,

    rolesOptions,
    formForAction,

    onCloseRoleModal,
    isOpenedRoleModal,
    onOpenAddRoleModal,
    handleAssignUserRole,

    isOpenedUnassignModal,
    handleUnassignUser,
    onOpenUnassignModal,
    onCloseUnassignModal,

    isOpenedUnassignRoleModal,
    onOpenUnassignRoleModal,
    onCloseUnassignRoleModal,
    handleUnassignRoleUser,

    isOpenedApiKeyModal,
    onCloseApiKeyModal,
    apiKey,
    handleGetApiKey,
    setApiKey,
  } = useOrganization(params.id);

  const handleCloseSecretModal = () => {
    onCloseApiKeyModal();
    setApiKey(null);
  };

  if (!organization || !params.id || loading) {
    return <>{loading && <Loading />}</>;
  }

  const breadcrumbs = (
    <Breadcrumbs
      className="mb-md text-small"
      subtitle="Organizations"
      subtitleUrl="/org_management"
      title={organization?.name ?? ''}
    />
  );

  return (
    <>
      <div className="border-b pb-lg mb-lg">
        <div className="flex gap-md flex-wrap justify-between items-center mb-md">
          {breadcrumbs}
        </div>
        <div className="flex flex-row grow">
          <SectionHeader
            className="grow"
            label={
              <div className="flex flex-row gap-x-md">
                {organization.name}
                <Status active={organization.active} />
              </div>
            }
            content={
              <div className="flex flex-row gap-x-md items-center">
                <Button
                  className="h-fit"
                  type="outline"
                  onClick={handleGetApiKey}
                  label="Show API key"
                />
                <div className="flex flex-row gap-x-lg items-center">
                  <Button
                    className="h-fit"
                    onClick={onOpen}
                    label="Assign user to Organization"
                  />
                  <Counter label="Total Users" value={organization.userCount} />
                  <Counter
                    label="Active Users"
                    value={organization.userCount}
                  />
                </div>
              </div>
            }
          />
        </div>
      </div>
      <ConfirmModal
        open={isOpenedUnassignModal}
        onClose={onCloseUnassignModal}
        onConfirm={handleUnassignUser}
        key={String(isOpenedUnassignModal) + '_unassign_user'}
        title={`Remove ${getUserName(formForAction)} from organization?`}
      />
      <Modal
        size="small"
        title="Assign user"
        open={isOpened}
        onClose={onClose}
        key={String(isOpened) + '_assign_user'}
      >
        <AssignUserForm
          saving={updating}
          users={userOptions}
          onCancel={onClose}
          onSave={handleAddUser}
        />
      </Modal>
      <Modal
        size="small"
        title="Assign role"
        open={isOpenedRoleModal}
        onClose={onCloseRoleModal}
        key={String(isOpenedRoleModal) + '_assign_role'}
      >
        <AssignUserRoleForm
          saving={updating}
          roles={rolesOptions}
          user={formForAction}
          onCancel={onCloseRoleModal}
          onSave={handleAssignUserRole}
        />
      </Modal>
      <Modal
        size="small"
        title="Remove user role"
        open={isOpenedUnassignRoleModal}
        onClose={onCloseUnassignRoleModal}
        key={String(isOpenedUnassignRoleModal) + '_unassign_role'}
      >
        <UnassignUserRoleForm
          saving={updating}
          roles={rolesOptions}
          user={formForAction}
          onCancel={onCloseUnassignRoleModal}
          onSave={form => handleUnassignRoleUser(form.roleId)}
        />
      </Modal>
      <Modal
        size="small"
        open={isOpenedApiKeyModal}
        onClose={handleCloseSecretModal}
        title="API key"
      >
        <div className="flex flex-col">
          <div className="flex flex-row gap-x-md justify-end">
            <Copy
              label="Copy key"
              className="text-buttonPrimary text-tiny"
              text={apiKey || ''}
            />
          </div>
          <div className="flex flex-col gap-y-md justify-center items-center my-md">
            <p className="text-center text-xl">{apiKey}</p>
          </div>
        </div>
        <div className="flex flex-row justify-center">
          <Button
            size="medium"
            type="contrast"
            label="Close"
            onClick={handleCloseSecretModal}
          />
        </div>
      </Modal>

      <UsersTab
        users={users}
        loading={loading}
        updating={updating}
        onOpenAddRoleModal={onOpenAddRoleModal}
        onOpenUnassignModal={onOpenUnassignModal}
        onOpenRemoveRoleModal={onOpenUnassignRoleModal}
      />
    </>
  );
};

export default Organization;
